import { Link, useLocation } from 'react-router-dom';
import { C, HEADER_HEIGHT, ink } from '../lib/theme';

/**
 * Sidebar de módulos do app (macro-navegação: "em qual zona eu estou"),
 * retrátil — colapsa pra só ícone. Substitui a antiga SideIndex ("Nesta
 * aba"), que era navegação de rolagem dentro de uma única página; esta é um
 * nível acima — entre módulos inteiros. CS é o Hub de auditoria de hoje;
 * Clientes é um módulo futuro (CRM/perfil de pagamento), ainda não
 * construído — ver PRD/korydevprd antes de tirar o rótulo "Em andamento" daqui.
 *
 * Estado de colapso é controlado pelo pai (App.tsx) — não local: o grid da
 * página precisa saber a largura atual pra devolver o espaço liberado ao
 * conteúdo, não só encolher os ícones deixando um vão vazio.
 */

/** Escudo com check — CS protegendo/confirmando receita (módulo ativo). */
function IconCS() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2.6 L19 5.4 V11 C19 16 16 19.6 12 21.4 C8 19.6 5 16 5 11 V5.4 Z"
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
      />
      <path d="M8.6 12 L10.9 14.3 L15.5 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Cartão de contato — perfil de cliente / ficha de CRM (módulo futuro). */
function IconClientes() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4.5" width="18" height="15" rx="4" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="9.4" cy="10.6" r="2.15" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.9 16.2c0.55-2.05 2.05-3.15 3.5-3.15s2.95 1.1 3.5 3.15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15.3 9.3h3.1M15.3 12.5h3.1M15.3 16h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
      <path d="M15 5 L8 12 L15 19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface ModuleDef {
  to: string;
  label: string;
  icon: () => React.JSX.Element;
  active: boolean;
  comingSoon?: boolean;
  /** Cor de identidade do módulo — CS usa o accent da marca; Clientes reaproveita
   * o azul já estabelecido pra PowerBot em theme.ts (ZONE.PowerBot), não uma cor nova. */
  accent: string;
  accentStrong: string;
}

function ModuleTile({ mod, collapsed }: { mod: ModuleDef; collapsed: boolean }) {
  const Icon = mod.icon;
  return (
    <Link
      to={mod.to} title={collapsed ? (mod.comingSoon ? `${mod.label} · Em andamento` : mod.label) : undefined}
      style={{
        display: 'flex', flexDirection: collapsed ? 'column' : 'row', alignItems: 'center',
        gap: collapsed ? 6 : 13, padding: collapsed ? '10px 6px' : '11px 13px', borderRadius: 17,
        textDecoration: 'none',
        background: mod.active ? ink(0.06) : 'transparent',
        border: `1px solid ${mod.active ? ink(0.09) : 'transparent'}`,
      }}
    >
      <span style={{
        width: 50, height: 50, flex: 'none', borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1.5px dashed ${mod.active ? `${mod.accent}70` : `${mod.accent}38`}`,
        background: mod.active
          ? `radial-gradient(circle at 35% 30%, ${mod.accentStrong}3c, transparent 72%)`
          : `radial-gradient(circle at 35% 30%, ${mod.accentStrong}18, transparent 72%)`,
        color: mod.accent,
        filter: mod.active ? `drop-shadow(0 0 9px ${mod.accent}70)` : `drop-shadow(0 0 4px ${mod.accent}30)`,
      }}>
        <Icon />
      </span>
      {!collapsed && (
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 750, color: mod.active ? C.fg : ink(0.7) }}>{mod.label}</span>
          {mod.comingSoon && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: mod.accent, background: `${mod.accent}1c`, border: `1px solid ${mod.accent}38`,
              borderRadius: 99, padding: '2px 7px', width: 'fit-content',
            }}>Em andamento</span>
          )}
        </span>
      )}
    </Link>
  );
}

export function AppSidebar({ collapsed, onToggleCollapsed }: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const location = useLocation();
  const isClientes = location.pathname.startsWith('/clientes');

  const modules: ModuleDef[] = [
    { to: '/fila', label: 'CS', icon: IconCS, active: !isClientes, accent: C.accent, accentStrong: C.accentStrong },
    { to: '/clientes', label: 'Clientes', icon: IconClientes, active: isClientes, comingSoon: true, accent: C.blueSoft, accentStrong: C.blue },
  ];

  return (
    <aside style={{
      position: 'sticky', top: HEADER_HEIGHT + 66, alignSelf: 'start', display: 'flex', flexDirection: 'column', gap: 5,
      minHeight: 'calc(100vh - 190px)',
    }}>
      {!collapsed && (
        <div style={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: ink(0.32), padding: '0 0 10px 4px',
        }}>Módulos</div>
      )}
      {modules.map((m) => <ModuleTile key={m.to} mod={m} collapsed={collapsed} />)}

      {/* Só o toggle de colapso fica no rodapé do trilho agora — o acesso a
          configurações/perfil saiu daqui (era redundante com o avatar do
          header, pedido explícito de remover a duplicidade). `marginTop:
          auto` mantém o botão ancorado embaixo mesmo com poucos módulos. */}
      <button
        type="button" onClick={onToggleCollapsed}
        title={collapsed ? 'Expandir' : 'Recolher'}
        style={{
          marginTop: 'auto', alignSelf: collapsed ? 'center' : 'flex-end', width: 26, height: 26, borderRadius: 9,
          border: `1px solid ${ink(0.1)}`, background: ink(0.04), color: ink(0.4),
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
        }}
      >
        <ChevronIcon collapsed={collapsed} />
      </button>
    </aside>
  );
}
